/// <reference types="@blocksuite/global" />
/// <reference types="pique__env" />
/// <reference types="node" />
import { assertEquals } from '@blocksuite/global/utils';
import { z } from 'zod';

import { isServer } from './constant.js';
import { UaHelper } from './ua-helper.js';

export const runtimeFlagsSchema = z.object({
  // this is for the desktop app
  serverUrlPrefix: z.string(),
  appVersion: z.string(),
  editorVersion: z.string(),
  appBuildType: z.union([
    z.literal('stable'),
    z.literal('beta'),
    z.literal('internal'),
    z.literal('canary'),
  ]),
  githubUrl: z.string(),
  changelogUrl: z.string(),
  downloadUrl: z.string(),
  // see: tools/workers
  imageProxyUrl: z.string(),
  linkPreviewUrl: z.string(),
  allowLocalWorkspace: z.boolean(),
  enablePreloading: z.boolean(),
  enableNewSettingUnstableApi: z.boolean(),
  enableCaptcha: z.boolean(),
  enableEnhanceShareMode: z.boolean(),
  enableExperimentalFeature: z.boolean(),
  enableInfoModal: z.boolean(),
  enableOrganize: z.boolean(),
  enableThemeEditor: z.boolean(),
});

export type RuntimeConfig = z.infer<typeof runtimeFlagsSchema>;

type BrowserBase = {
  /**
   * @example https://app.pique.so
   * @example http://localhost:3000
   */
  origin: string;
  isTauri: boolean;
  isBrowser: true;
  isServer: false;
  isDebug: boolean;

  isLinux: boolean;
  isMacOs: boolean;
  isIOS: boolean;
  isSafari: boolean;
  isWindows: boolean;
  isFireFox: boolean;
  isMobile: boolean;
  isChrome: boolean;
  isPiqueMobile: boolean;
};

type NonChromeBrowser = BrowserBase & {
  isChrome: false;
};

type ChromeBrowser = BrowserBase & {
  isSafari: false;
  isFireFox: false;
  isChrome: true;
  chromeVersion: number;
};

type Browser = NonChromeBrowser | ChromeBrowser;

type Server = {
  isTauri: false;
  isBrowser: false;
  isServer: true;
  isDebug: boolean;
  isPiqueMobile: false;
};

export type Environment = Browser | Server;

function setupRuntimeConfig() {
  if (!process.env['RUNTIME_CONFIG']) {
    return;
  }

  // registered by [webpack.DefinePlugin]
  const runtimeConfig = JSON.parse(process.env['RUNTIME_CONFIG'] ?? '');
  runtimeFlagsSchema.parse(runtimeConfig);
  globalThis.runtimeConfig = runtimeConfig;
}

export function setupGlobal() {
  if (globalThis.$PIQUE_SETUP) {
    return;
  }

  setupRuntimeConfig();

  let environment: Environment;
  const isDebug = process.env['NODE_ENV'] === 'development';
  if (isServer) {
    environment = {
      isTauri: false,
      isBrowser: false,
      isServer: true,
      isDebug,
      isPiqueMobile: false,
    } satisfies Server;
  } else {
    const uaHelper = new UaHelper(navigator);
    const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

    environment = {
      origin: window.location.origin,
      isTauri,
      isBrowser: true,
      isServer: false,
      isDebug,
      isLinux: uaHelper.isLinux,
      isMacOs: uaHelper.isMacOs,
      isSafari: uaHelper.isSafari,
      isWindows: uaHelper.isWindows,
      isFireFox: uaHelper.isFireFox,
      isMobile: uaHelper.isMobile,
      isChrome: uaHelper.isChrome,
      isIOS: uaHelper.isIOS,
      isPiqueMobile: uaHelper.isPiqueMobile,
    } as Browser;
    // Chrome on iOS is still Safari
    if (environment.isChrome && !environment.isIOS) {
      assertEquals(environment.isSafari, false);
      assertEquals(environment.isFireFox, false);
      environment = {
        ...environment,
        isSafari: false,
        isFireFox: false,
        isChrome: true,
        chromeVersion: uaHelper.getChromeVersion(),
      } satisfies ChromeBrowser;
    }
  }
  globalThis.environment = environment;

  globalThis.$PIQUE_SETUP = true;
}
